import clsx from 'clsx';
import { PagePreviewThumb } from './PagePreviewThumb';
import { clusterTint } from './clusterTints';
import { usePagePreviewHover } from '../../components/pagePreview/usePagePreviewHover';
import { splitCluster, type PreviewLayoutProps } from './previewHelpers';

// Flow layout: clusters render as continuous inline runs. Pages flow like words,
// a cluster can wrap onto a second line, and the next cluster begins on the same
// row — no forced break. The tint band sits on each page wrapper (an inline-block,
// full height by construction) so it fills the whole thumbnail and survives wraps.
// Every item reserves a fixed-height header row at the top; only the first item
// of a cluster fills it with the id badge, so all items stay the same height and
// the id sits inside the tint band. Horizontal padding is uniform across items
// (first/last only add corner radius) — otherwise aspect-ratio would make the
// padded end items shorter than the middle ones.
export function PagePreviewsFlow({ pagesGrouped, category, tint, compact, expanded, onExpand, resize }: PreviewLayoutProps) {
  const { show, hide } = usePagePreviewHover();
  return (
    <div className="leading-none">
      {pagesGrouped.map(([clusterId, clusterPages], index) => {
        const { shown, hidden } = splitCluster(clusterPages, clusterId, compact, expanded);
        const t = clusterTint(index);
        const itemCount = shown.length + (hidden > 0 ? 1 : 0);

        return (
          <span key={clusterId} data-cluster-id={clusterId} id={`cluster-${clusterId}`} className="mr-3 inline align-top">
            {shown.map((page, i) => (
              <div
                key={page.id}
                data-page-card
                onMouseEnter={(e) => show(page, e.currentTarget.getBoundingClientRect())}
                onMouseLeave={hide}
                className={clsx(
                  'mb-2.5 inline-block w-(--thumb-w) cursor-pointer px-1.5 pt-1.5 pb-2 align-top',
                  tint && t.bg,
                  i === 0 && 'rounded-l-xl',
                  i === itemCount - 1 && 'rounded-r-xl',
                )}
              >
                <div className="relative h-[22px]">
                  {i === 0 && (
                    <span
                      className={clsx(
                        'absolute left-0 top-0 z-10 inline-flex h-[18px] items-center whitespace-nowrap rounded-md px-2 text-[11px] font-bold text-white',
                        t.chip,
                      )}
                      title={`Cluster ${clusterId}`}
                    >
                      ⬡ {clusterId}
                    </span>
                  )}
                </div>
                <PagePreviewThumb page={page} category={category} resize={resize} />
              </div>
            ))}

            {hidden > 0 && (
              <button
                type="button"
                onClick={() => onExpand(clusterId)}
                className={clsx('mb-2.5 inline-block w-(--thumb-w) cursor-pointer rounded-r-xl px-1.5 pt-1.5 pb-2 align-top', tint && t.bg)}
              >
                <div className="h-[22px]" />
                <div className="flex aspect-3/4 items-center justify-center rounded-md border border-dashed border-gray-300 text-sm font-bold text-gray-500 dark:border-zinc-600 dark:text-zinc-400">
                  +{hidden}
                </div>
                <div className="mt-1 text-xs">&nbsp;</div>
                <div className="text-xs">&nbsp;</div>
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}
